import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { ButtonBase, Flex, Icon, Spinner, Text } from '@fullstory/components';
import { classNames } from '@fullstory/util/fmt';
import { useInterval, useHover } from '@fullstory/react-hooks';
import { assertExhaustive } from '@fullstory/typeutils';
import { ButtonStatus } from '../../utils';
import css from './index.module.scss';
import { Duration, duration, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { useIntl } from 'react-intl';

interface SearchieCacheButtonProps {
  nowFunc: () => Date;
  status: ButtonStatus;
  cardHover: boolean;
  refreshableAfter: duration; // duration after timestamp, relative to nowFunc(), that the ability to refresh is available
  onShowClick: () => void;
  onRefreshClick: () => void;
  timestamp?: timestamp; // timestamp must be provided if status is 'done' or 'hasData'
}

export const SearchieCacheButton: React.FC<SearchieCacheButtonProps> = ({
  nowFunc,
  status,
  cardHover,
  onShowClick,
  onRefreshClick,
  timestamp,
  refreshableAfter,
}) => {
  const intl = useIntl();
  const { hovered, hoverProps } = useHover();
  const [, setNow] = useState(nowFunc()); // setNow is solely used to trigger periodic re-rendering
  const timestampDate = Timestamp.toDate(timestamp);
  const now = nowFunc();
  const ageMillis: number = timestampDate ? now.getTime() - timestampDate.getTime() : 0;
  const refreshable = timestamp != null && ageMillis > Duration.toMillis(refreshableAfter);

  const updateNow = useCallback(() => {
    setNow(nowFunc());
  }, [setNow, nowFunc]);
  useInterval(updateNow, 60 * 1000);

  // while we tick every minute, we do want a more precise update based on the exact time we expect the cache to be refreshable
  const refreshableAtMillis = timestampDate
    ? timestampDate.getTime() + Duration.toMillis(refreshableAfter)
    : null;
  useEffect(() => {
    if (refreshableAtMillis) {
      const timeoutMillis = refreshableAtMillis - nowFunc().getTime();
      if (timeoutMillis > 0) {
        const timeout = setTimeout(updateNow, timeoutMillis);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
    return () => {};
  }, [updateNow, nowFunc, refreshableAtMillis]);

  const config = useMemo((): {
    icon: ComponentProps<typeof Icon>['name'] | 'spinner';
    label: string | undefined;
    onSelect: (() => void) | undefined;
  } | null => {
    const timestampText = moment(timestampDate ?? new Date()).from(now);
    switch (status) {
      case ButtonStatus.hidden:
        return null;
      case ButtonStatus.isFetching:
        return {
          label: intl.formatMessage({ defaultMessage: 'Fetching new data...', id: 'WUJtEO' }),
          icon: 'spinner',
          onSelect: undefined,
        };
      case ButtonStatus.hasData:
        return {
          label: hovered
            ? intl.formatMessage({ defaultMessage: 'Show new data', id: 'J5w3q1' })
            : timestampText,
          icon: 'clock',
          onSelect: onShowClick,
        };
      case ButtonStatus.done:
        return {
          label:
            hovered && refreshable
              ? intl.formatMessage({ defaultMessage: 'Refresh data', id: 'PTtxt/' })
              : timestampText,
          icon: hovered && refreshable ? 'refresh' : 'clock',
          onSelect: refreshable ? onRefreshClick : undefined,
        };
      case ButtonStatus.fetchFailed:
        return {
          label:
            hovered || !timestamp
              ? intl.formatMessage({
                  defaultMessage: 'Failed to refresh. Try again?',
                  id: 'RLqafw',
                })
              : timestampText,
          icon: hovered ? 'refresh' : 'clock',
          onSelect: onRefreshClick,
        };
      default:
        assertExhaustive(status);
    }
  }, [
    status,
    hovered,
    timestampDate,
    now,
    onShowClick,
    onRefreshClick,
    refreshable,
    timestamp,
    intl,
  ]);

  // Hidden state
  if (!config) {
    return null;
  }

  const { icon, label, onSelect } = config;
  const actionable = !!onSelect;
  const buttonStatusStr = ButtonStatus[status] ?? '';
  const labelColor: ComponentProps<typeof Text>['color'] =
    hovered && actionable ? 'cta' : cardHover ? 'secondary' : 'disabled';
  const showLabel = hovered || cardHover;
  const iconColor: ComponentProps<typeof Icon>['color'] = labelColor;

  return (
    <ButtonBase
      data-component="SearchieCacheButton"
      {...hoverProps}
      testId="searchie-cache-button"
      onClick={onSelect}
      className={classNames(css.button, {
        [css.hovered]: hovered && actionable,
      })}
      data-button-status={buttonStatusStr}
      data-age-millis={ageMillis}
      data-fs-properties-schema='{"data-button-status":"str","data-age-millis":"int"}'
    >
      <Flex alignItems="center" gap={8}>
        {/* Adding a little padding for visual alignment */}
        <div style={{ paddingBottom: 1 }}>
          {icon === 'spinner' ? (
            <Spinner diameter={20} />
          ) : (
            <Icon name={icon} color={iconColor} size={20} />
          )}
        </div>
        {showLabel && label && (
          <Text appearance="bodySmall" color={labelColor}>
            {label}
          </Text>
        )}
      </Flex>
    </ButtonBase>
  );
};
